import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCity,
  FaGlobe,
  FaMapPin,
  FaLock,
  FaCheckCircle,
  FaTag,
} from 'react-icons/fa';
import CONFIG from './../../config.js';

function UserSignup() {
  const [formData, setFormData] = useState({
    name: '',
    mobile_number: '',
    alt_mobile_number: '',
    email: '',
    address: '',
    city: '',
    area: '',
    state: '',
    pincode: '',
    password: '',
    agreeTerms: '',
  });

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponState, setCouponState] = useState(null); // null | { valid, discount_percent, discount_type, message }
  const [couponChecking, setCouponChecking] = useState(false);
  const navigate = useNavigate();
  const BASE_URL = CONFIG.API_BASE_URL;

  // ── Mobile OTP gate — the Password field (the account's 4-digit PIN) stays
  // locked until this mobile number is verified. Mirrors UserLogin.js's new
  // mobile+PIN flow: OTP is only ever required to establish/verify identity,
  // never on every normal login. Runs as a popup so it reads as a distinct
  // verification step rather than more inline form fields.
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpMessage, setOtpMessage] = useState(null); // { text, ok }
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0); // seconds until "Resend OTP" is clickable again
  const cooldownTimerRef = useRef(null);

  // 120s resend cooldown — ticks down while the modal's OTP step is active.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    cooldownTimerRef.current = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(cooldownTimerRef.current);
  }, [resendCooldown]);

  const AREA_OPTIONS = CONFIG.AREA_OPTIONS || {};
  const cityOptions = Object.keys(AREA_OPTIONS);
  const selectedCityAreas = AREA_OPTIONS[formData.city]?.areas || [];

  const styles = {
    page: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eef2ff 100%)',
      padding: '30px 20px',
      fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    },
    wrapper: {
      maxWidth: '1500px',
      width: '96%',
      margin: '0 auto',
    },
    hero: {
      background: 'linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)',
      color: '#fff',
      borderRadius: '28px',
      padding: '34px 36px',
      boxShadow: '0 20px 40px rgba(37, 99, 235, 0.22)',
      marginBottom: '24px',
    },
    heroBadge: {
      display: 'inline-block',
      background: 'rgba(255,255,255,0.14)',
      padding: '7px 14px',
      borderRadius: '999px',
      fontSize: '12px',
      fontWeight: '700',
      marginBottom: '12px',
    },
    heroTitle: {
      margin: 0,
      fontSize: '2.1rem',
      fontWeight: '800',
    },
    heroText: {
      marginTop: '10px',
      marginBottom: 0,
      color: 'rgba(255,255,255,0.9)',
      maxWidth: '900px',
      lineHeight: 1.6,
      fontSize: '15px',
    },
    formCard: {
      background: '#fff',
      borderRadius: '28px',
      border: '1px solid #e5edf8',
      boxShadow: '0 14px 34px rgba(15, 23, 42, 0.08)',
      overflow: 'hidden',
      width: '100%',
      maxWidth: '1320px',
      margin: '0 auto',
    },
    formHeader: {
      padding: '28px 40px',
      borderBottom: '1px solid #edf2f7',
      background: 'linear-gradient(180deg, #ffffff, #f8fbff)',
    },
    formTitle: {
      margin: 0,
      fontSize: '1.6rem',
      fontWeight: '800',
      color: '#0f172a',
    },
    formSub: {
      margin: '8px 0 0 0',
      color: '#64748b',
      fontSize: '14px',
    },
    body: {
      padding: '32px 40px 36px',
    },
    section: {
      marginBottom: '28px',
      padding: '24px',
      borderRadius: '20px',
      background: '#f8fbff',
      border: '1px solid #e6eef8',
    },
    sectionTitle: {
      margin: '0 0 18px 0',
      fontSize: '1.1rem',
      fontWeight: '800',
      color: '#0f172a',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      alignItems: 'start',
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#334155',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    input: {
      width: '100%',
      padding: '14px 16px',
      borderRadius: '14px',
      border: '1px solid #d6deeb',
      fontSize: '15px',
      outline: 'none',
      background: '#fff',
      boxSizing: 'border-box',
    },
    select: {
      width: '100%',
      padding: '14px 16px',
      borderRadius: '14px',
      border: '1px solid #d6deeb',
      fontSize: '15px',
      outline: 'none',
      background: '#fff',
      boxSizing: 'border-box',
    },
    error: {
      color: '#dc2626',
      fontSize: '13px',
      marginTop: '2px',
      fontWeight: '600',
    },
    termsBox: {
      marginTop: '8px',
      padding: '16px 18px',
      borderRadius: '16px',
      background: '#fff',
      border: '1px solid #dbe7f5',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    termsRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      color: '#334155',
      fontSize: '14px',
      lineHeight: 1.6,
    },
    link: {
      color: '#2563eb',
      fontWeight: '700',
      textDecoration: 'none',
    },
    couponRow: {
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-start',
    },
    couponInput: {
      flex: 1,
      padding: '14px 16px',
      borderRadius: '14px',
      border: '1px solid #d6deeb',
      fontSize: '15px',
      outline: 'none',
      background: '#fff',
      boxSizing: 'border-box',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: '700',
    },
    couponBtn: {
      padding: '14px 20px',
      borderRadius: '14px',
      border: 'none',
      background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
      color: '#fff',
      fontWeight: '800',
      fontSize: '14px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    couponBtnDisabled: {
      background: '#c4b5fd',
      cursor: 'not-allowed',
    },
    couponSuccess: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
      padding: '10px 14px',
      borderRadius: '12px',
      background: '#f0fdf4',
      border: '1px solid #86efac',
      color: '#15803d',
      fontSize: '13px',
      fontWeight: '700',
    },
    couponError: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
      padding: '10px 14px',
      borderRadius: '12px',
      background: '#fef2f2',
      border: '1px solid #fca5a5',
      color: '#dc2626',
      fontSize: '13px',
      fontWeight: '700',
    },
    discountBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 12px',
      borderRadius: '999px',
      background: '#dcfce7',
      color: '#15803d',
      fontSize: '13px',
      fontWeight: '800',
      marginLeft: '6px',
    },
    submitWrap: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '30px',
    },
    submitBtn: {
      border: 'none',
      borderRadius: '999px',
      background: loading ? '#93c5fd' : 'linear-gradient(135deg, #1d4ed8, #2563eb)',
      color: '#fff',
      padding: '15px 34px',
      fontSize: '15px',
      fontWeight: '800',
      minWidth: '240px',
      boxShadow: '0 12px 24px rgba(37, 99, 235, 0.24)',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.85 : 1,
    },

    // ── OTP verification popup ──────────────────────────────────────────────
    modalOverlay: {
      position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 9999,
    },
    modalBox: {
      width: '100%', maxWidth: '400px', background: '#fff', borderRadius: '22px',
      padding: '26px 24px 22px', boxShadow: '0 20px 50px rgba(0,0,0,0.22)',
      position: 'relative', textAlign: 'center',
    },
    modalClose: {
      position: 'absolute', top: '12px', right: '12px', width: '34px', height: '34px', border: 'none',
      borderRadius: '50%', background: '#f1f5f9', color: '#334155', display: 'flex', alignItems: 'center',
      justifyContent: 'center', cursor: 'pointer', fontSize: '14px',
    },
    modalIcon: {
      width: '58px', height: '58px', borderRadius: '50%', margin: '4px auto 14px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', background: '#dbeafe', color: '#1d4ed8', fontSize: '24px',
    },
    modalTitle: { margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '800', color: '#0f172a' },
    modalSub: { margin: '0 0 18px 0', fontSize: '13.5px', color: '#64748b', lineHeight: 1.6 },
    modalOtpInput: {
      width: '100%', height: '54px', padding: '0 16px', borderRadius: '14px', border: '1px solid #cbd5e1',
      background: '#f8fafc', fontSize: '20px', fontWeight: '700', letterSpacing: '4px', textAlign: 'center',
      color: '#0f172a', outline: 'none', boxSizing: 'border-box', marginBottom: '14px',
    },
    modalVerifyBtn: {
      width: '100%', height: '50px', border: 'none', borderRadius: '14px',
      background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', fontSize: '15px',
      fontWeight: '800', cursor: 'pointer', marginBottom: '10px',
    },
    modalResendBtn: {
      width: '100%', background: 'none', border: 'none', fontSize: '13px', fontWeight: '700',
      color: '#2563eb', cursor: 'pointer', padding: '6px',
    },
  };

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;
    if (!/^\d{10}$/.test(formData.mobile_number)) {
      setCouponState({ valid: false, message: 'Enter a valid mobile number first.' });
      return;
    }
    setCouponChecking(true);
    setCouponState(null);
    try {
      const res = await fetch(`${BASE_URL}/api/user/validate-coupon/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coupon_code: code, mobile_number: formData.mobile_number }),
      });
      const data = await res.json();
      setCouponState(data);
    } catch {
      setCouponState({ valid: false, message: 'Could not verify coupon. Try again.' });
    } finally {
      setCouponChecking(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setAgreeTerms(checked);
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));

      if (errors.terms) {
        setErrors((prev) => ({
          ...prev,
          terms: '',
        }));
      }
      return;
    }

    let updatedValue = value;

    if (['mobile_number', 'alt_mobile_number', 'pincode'].includes(name)) {
      updatedValue = value.replace(/\D/g, '');
    }

    if (name === 'mobile_number') {
      setCouponState(null);
      // Changing the number after sending/verifying an OTP invalidates both —
      // the OTP was issued for the old number.
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
      setOtpMessage(null);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Opens the popup and sends the first OTP in one action — also used as
  // "Resend OTP" from inside the modal once the 120s cooldown clears.
  const handleSendOtp = async () => {
    if (!/^\d{10}$/.test(formData.mobile_number)) {
      setErrors((prev) => ({ ...prev, mobile_number: 'Enter a valid 10-digit number first' }));
      return;
    }
    setShowOtpModal(true);
    setOtpSending(true);
    setOtpMessage(null);
    try {
      const res = await fetch(`${BASE_URL}/api/user/request-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: formData.mobile_number }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpSent(true);
        setOtp('');
        setResendCooldown(120);
        setOtpMessage({ text: 'OTP sent to your mobile number.', ok: true });
      } else {
        setOtpMessage({ text: data.error || 'Could not send OTP.', ok: false });
      }
    } catch {
      setOtpMessage({ text: 'Could not send OTP. Please try again.', ok: false });
    } finally {
      setOtpSending(false);
    }
  };

  // Just hides the popup — the resend cooldown keeps ticking in the
  // background (it's state on this component, not the modal's own
  // lifecycle), so reopening shows the correct remaining time rather than
  // a frozen one.
  const closeOtpModal = () => setShowOtpModal(false);

  const handleVerifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setOtpMessage({ text: 'Enter the 6-digit OTP.', ok: false });
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await fetch(`${BASE_URL}/api/user/verify-otp/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: formData.mobile_number, otp }),
      });
      const data = await res.json();
      if (res.ok) {
        setOtpVerified(true);
        setOtpMessage({ text: 'Mobile number verified ✓', ok: true });
        // Brief success flash inside the popup, then close and drop back to
        // the form with the Password field now unlocked.
        setTimeout(() => setShowOtpModal(false), 900);
      } else {
        // Wrong OTP — leave the popup open so they can retry immediately,
        // the resend cooldown is independent of failed verify attempts.
        setOtpMessage({ text: data.error || 'Incorrect OTP. Please try again.', ok: false });
      }
    } catch {
      setOtpMessage({ text: 'Could not verify OTP. Please try again.', ok: false });
    } finally {
      setOtpVerifying(false);
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData((prev) => ({
      ...prev,
      password: value,
    }));

    if (errors.password) {
      setErrors((prev) => ({
        ...prev,
        password: '',
      }));
    }
  };

  const handleCityChange = (e) => {
    const selectedCity = e.target.value;

    setFormData((prev) => ({
      ...prev,
      city: selectedCity,
      area: '',
    }));

    setErrors((prev) => ({
      ...prev,
      city: '',
      area: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!/^\d{10}$/.test(formData.mobile_number)) newErrors.mobile_number = 'Enter valid 10-digit number';
    if (!/^\d{10}$/.test(formData.alt_mobile_number)) newErrors.alt_mobile_number = 'Enter valid 10-digit number';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.area) newErrors.area = 'Area is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Enter valid 6-digit pincode';
    if (!/^\d{4}$/.test(formData.password)) newErrors.password = 'Password must be exactly 4 digits';
    if (!otpVerified) newErrors.password = newErrors.password || 'Verify your mobile number with OTP first';
    if (!agreeTerms) newErrors.terms = 'You must agree to Terms & Conditions';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const updatedFormData = {
      ...formData,
      otp,
      agreeTerms: agreeTerms ? 'true' : 'false',
      coupon_code: couponState?.valid ? couponCode.trim().toUpperCase() : '',
    };

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/api/user/user-signup/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedFormData),
      });

      const data = await res.json();

      if (res.ok) {
        // Same session bootstrap as UserLogin.js's completeLogin — signup
        // now returns a token too, so the new account logs straight in
        // instead of bouncing back to a separate login screen.
        const userData = {
          id: data.user_id || '',
          name: data.user_name || formData.name,
          full_name: data.user_name || formData.name,
          mobile: data.mobile_number || formData.mobile_number,
        };
        localStorage.setItem('currentUser', JSON.stringify(userData));
        if (data.token) localStorage.setItem('authToken', data.token);
        sessionStorage.setItem('isUserLoggedIn', 'true');
        sessionStorage.setItem('user_id', userData.id);
        sessionStorage.setItem('userName', userData.name);
        sessionStorage.setItem('mobile', userData.mobile);
        window.dispatchEvent(new Event('auth-updated'));

        alert('Signup Successful');
        navigate('/');
      } else {
        alert('Signup Failed: ' + (data.error || data.message || 'Something went wrong'));
      }
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred during signup.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div style={styles.page}>
      <div style={styles.wrapper}>
        <div style={styles.hero}>
          <div style={styles.heroBadge}>User Signup</div>
          <h1 style={styles.heroTitle}>Create Your User Account</h1>
          <p style={styles.heroText}>
            Enter your personal details, location, and account credentials to complete
            your registration and start using the platform.
          </p>
        </div>

        <form style={styles.formCard} onSubmit={handleSubmit}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>User Registration Form</h2>
            <p style={styles.formSub}>
              Fill all required fields carefully to complete your signup.
            </p>
          </div>

          <div style={styles.body}>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Personal Details</h3>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}><FaUser /> Name</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && <span style={styles.error}>{errors.name}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaPhoneAlt /> Mobile Number</label>
                  <div style={styles.couponRow}>
                    <input
                      style={{ ...styles.input, opacity: otpVerified ? 0.7 : 1 }}
                      type="text"
                      name="mobile_number"
                      value={formData.mobile_number}
                      onChange={handleChange}
                      maxLength={10}
                      inputMode="numeric"
                      disabled={otpVerified}
                    />
                    <button
                      type="button"
                      style={{
                        ...styles.couponBtn,
                        ...(otpSending || otpVerified || !/^\d{10}$/.test(formData.mobile_number) ? styles.couponBtnDisabled : {}),
                      }}
                      disabled={otpSending || otpVerified || !/^\d{10}$/.test(formData.mobile_number)}
                      onClick={handleSendOtp}
                    >
                      {otpVerified ? 'Verified ✓' : otpSending ? 'Sending…' : otpSent ? 'Resend OTP' : 'Send OTP'}
                    </button>
                  </div>
                  {errors.mobile_number && <span style={styles.error}>{errors.mobile_number}</span>}

                  {/* The actual send/verify flow runs in the popup below —
                      this is just the persistent post-verification state,
                      so it doesn't disappear once the modal closes. The
                      Password/PIN field stays locked until this is true. */}
                  {otpVerified && (
                    <div style={styles.couponSuccess}>✓ Mobile number verified ✓</div>
                  )}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaPhoneAlt /> Alternative Mobile Number</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="alt_mobile_number"
                    value={formData.alt_mobile_number}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                  />
                  {errors.alt_mobile_number && <span style={styles.error}>{errors.alt_mobile_number}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaEnvelope /> Email</label>
                  <input
                    style={styles.input}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <span style={styles.error}>{errors.email}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaLock /> Password (4-digit PIN)</label>
                  <input
                    style={{ ...styles.input, opacity: otpVerified ? 1 : 0.6 }}
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handlePasswordChange}
                    maxLength={4}
                    inputMode="numeric"
                    placeholder={otpVerified ? 'Enter 4-digit password' : 'Verify your mobile number first'}
                    disabled={!otpVerified}
                  />
                  {errors.password && <span style={styles.error}>{errors.password}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaMapMarkerAlt /> Address</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                  {errors.address && <span style={styles.error}>{errors.address}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaCity /> City</label>
                  <select
                    style={styles.select}
                    name="city"
                    value={formData.city}
                    onChange={handleCityChange}
                  >
                    <option value="">Select City</option>
                    {cityOptions.map((cityKey) => (
                      <option key={cityKey} value={cityKey}>
                        {cityKey.charAt(0).toUpperCase() + cityKey.slice(1)}
                      </option>
                    ))}
                  </select>
                  {errors.city && <span style={styles.error}>{errors.city}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaMapPin /> Area</label>
                  <select
                    style={styles.select}
                    name="area"
                    value={formData.area}
                    onChange={handleChange}
                    disabled={!formData.city}
                  >
                    <option value="">Select Area</option>
                    {selectedCityAreas.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                  {errors.area && <span style={styles.error}>{errors.area}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaGlobe /> State</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  />
                  {errors.state && <span style={styles.error}>{errors.state}</span>}
                </div>

                <div style={styles.field}>
                  <label style={styles.label}><FaMapPin /> Pincode</label>
                  <input
                    style={styles.input}
                    type="text"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    maxLength={6}
                    inputMode="numeric"
                  />
                  {errors.pincode && <span style={styles.error}>{errors.pincode}</span>}
                </div>
              </div>

              {/* ── Coupon Code ── */}
              <div style={{ marginTop: '20px' }}>
                <label style={styles.label}><FaTag /> Coupon Code <span style={{ color: '#94a3b8', fontWeight: 500, fontSize: '12px' }}>(optional)</span></label>
                <div style={styles.couponRow}>
                  <input
                    style={styles.couponInput}
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value);
                      setCouponState(null);
                    }}
                    maxLength={30}
                  />
                  <button
                    type="button"
                    style={{
                      ...styles.couponBtn,
                      ...(couponChecking || !couponCode.trim() ? styles.couponBtnDisabled : {}),
                    }}
                    disabled={couponChecking || !couponCode.trim()}
                    onClick={handleApplyCoupon}
                  >
                    {couponChecking ? 'Checking…' : 'Apply'}
                  </button>
                </div>
                {couponState && (
                  couponState.valid ? (
                    <div style={styles.couponSuccess}>
                      ✓ {couponState.message}
                      <span style={styles.discountBadge}>
                        -{couponState.discount_percent}% off
                        {couponState.discount_type === 'influencer' && couponState.influencer_name
                          ? ` · via ${couponState.influencer_name}`
                          : ''}
                      </span>
                    </div>
                  ) : (
                    <div style={styles.couponError}>✕ {couponState.message}</div>
                  )
                )}
              </div>

              <div style={{ marginTop: '20px' }}>
                <label style={styles.label}><FaCheckCircle /> Terms & Conditions</label>
                <div style={styles.termsBox}>
                  <div style={styles.termsRow}>
                    <input
                      type="checkbox"
                      id="termsCheck"
                      name="agreeTerms"
                      checked={agreeTerms}
                      onChange={handleChange}
                      style={{ width: '16px', height: '16px', marginTop: '3px' }}
                    />
                    <label htmlFor="termsCheck" style={{ margin: 0 }}>
                      I agree to the{' '}
                      <a
                        href="http://www.digilaboursolutions.com.s3-website.ap-south-1.amazonaws.com/terms-conditions.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.link}
                      >
                        Terms and Conditions
                      </a>
                    </label>
                  </div>
                  {errors.terms && <span style={styles.error}>{errors.terms}</span>}
                </div>
              </div>
            </div>

            <div style={styles.submitWrap}>
              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading ? 'Submitting...' : 'Signup'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    {/* ── Mobile OTP verification popup ─────────────────────────────────────── */}
    {showOtpModal && (
      <div style={styles.modalOverlay} onClick={closeOtpModal}>
        <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
          <button type="button" style={styles.modalClose} onClick={closeOtpModal}>✕</button>
          <div style={styles.modalIcon}><FaPhoneAlt /></div>
          <h3 style={styles.modalTitle}>Verify Mobile Number</h3>
          <p style={styles.modalSub}>
            {otpVerified
              ? 'Your mobile number has been verified.'
              : `Enter the 6-digit OTP sent to ${formData.mobile_number}.`}
          </p>

          {!otpVerified && (
            <>
              <input
                style={styles.modalOtpInput}
                type="text"
                placeholder="••••••"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                inputMode="numeric"
                autoFocus
              />
              <button
                type="button"
                style={{
                  ...styles.modalVerifyBtn,
                  ...(otpVerifying || otp.length !== 6 ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
                }}
                disabled={otpVerifying || otp.length !== 6}
                onClick={handleVerifyOtp}
              >
                {otpVerifying ? 'Verifying…' : 'Verify OTP'}
              </button>
              <button
                type="button"
                style={{
                  ...styles.modalResendBtn,
                  ...(resendCooldown > 0 || otpSending ? { color: '#94a3b8', cursor: 'not-allowed' } : {}),
                }}
                disabled={resendCooldown > 0 || otpSending}
                onClick={handleSendOtp}
              >
                {otpSending ? 'Sending…' : resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </>
          )}

          {otpMessage && (
            <div style={{ marginTop: '12px', ...(otpMessage.ok ? styles.couponSuccess : styles.couponError) }}>
              {otpMessage.ok ? '✓' : '✕'} {otpMessage.text}
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}

export default UserSignup;